import { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { ClockIcon, UserIcon, FolderIcon, LinkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { db } from '../../config/firebaseConfig';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
  'Done': { bg: '#00c875', text: '#fff' },
  'Working on it': { bg: '#fdab3d', text: '#fff' },
  'Stuck': { bg: '#e2445c', text: '#fff' },
  'Waiting For Info': { bg: '#0086c0', text: '#fff' },
  'Not Started Yet': { bg: '#c4c4c4', text: '#fff' }
};

const TaskViewModal = ({ show, handleClose, task, onTaskUpdate }) => {
  const { userData } = useAuth();
  const [localStatus, setLocalStatus] = useState('');
  const [localLink, setLocalLink] = useState('');
  const [message, setMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (task) {
      setLocalStatus(task.status || 'Not Started Yet');
      setLocalLink(task.link || '');
    }
  }, [task]);

  // Otomatik olarak son mesaja kaydırma
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [task?.messages]);

  if (!task) return null;
  const style = STATUS_COLORS[localStatus] || STATUS_COLORS['Not Started Yet'];

  const handleUpdateField = async (field, value) => {
    setIsUpdating(true);
    try {
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, { [field]: value });
      if (onTaskUpdate) onTaskUpdate(); // Ana listeyi yenile
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsUpdating(true);
    const newMessage = {
      text: message,
      senderId: userData?.uid || 'unknown',
      senderName: userData?.displayName?.split(' ')[0] || userData?.email?.split('@')[0] || 'User',
      timestamp: new Date().toISOString()
    };

    try {
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, {
        messages: arrayUnion(newMessage)
      });
      setMessage('');
      if (onTaskUpdate) onTaskUpdate(); // Ana listeyi yenile
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'No deadline set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold fs-4 text-dark">{task.title}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="pt-3">
        <div className="row g-4">
          
          {/* SOL PANEL: Detaylar ve Güncellemeler */}
          <div className="col-lg-6">
            <h6 className="fw-bold text-muted mb-3 text-uppercase" style={{ fontSize: '12px', letterSpacing: '1px' }}>Task Details</h6>
            
            <div className="d-flex flex-column gap-3 mb-4">
              {/* Status Update */}
              <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-4">
                <span className="fw-bold text-secondary">Status</span>
                <select 
                    value={localStatus} 
                    onChange={(e) => {
                        setLocalStatus(e.target.value);
                        handleUpdateField('status', e.target.value);
                    }}
                    disabled={isUpdating}
                    className="form-select border-0 text-center fw-bold shadow-sm"
                    style={{ backgroundColor: style.bg, color: style.text, width: '180px', cursor: 'pointer', borderRadius: '20px' }}
                >
                    {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s} style={{backgroundColor: '#fff', color: '#333'}}>{s}</option>)}
                </select>
              </div>

              {/* Info Rows */}
              <div className="d-flex align-items-center p-3 bg-light rounded-4 gap-3">
                <UserIcon className="text-primary" style={{ width: '20px' }} />
                <div className="flex-grow-1"><p className="mb-0 text-muted small fw-bold">Assignee</p></div>
                <div className="fw-medium">{task.assigneeName || 'Unassigned'}</div>
              </div>

              <div className="d-flex align-items-center p-3 bg-light rounded-4 gap-3">
                <ClockIcon className="text-danger" style={{ width: '20px' }} />
                <div className="flex-grow-1"><p className="mb-0 text-muted small fw-bold">Deadline</p></div>
                <div className="fw-medium">{formatDateTime(task.dueDate)}</div>
              </div>

              <div className="d-flex align-items-center p-3 bg-light rounded-4 gap-3">
                <FolderIcon className="text-warning" style={{ width: '20px' }} />
                <div className="flex-grow-1"><p className="mb-0 text-muted small fw-bold">Group</p></div>
                <div className="fw-medium">{task.group}</div>
              </div>

              {/* Quick Link Update */}
              <div className="p-3 bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <LinkIcon className="text-primary" style={{ width: '18px' }} />
                  <span className="fw-bold text-primary">Relevant Link</span>
                </div>
                <div className="input-group input-group-sm">
                  <input type="url" className="form-control border-0 shadow-none rounded-start" placeholder="https://..." value={localLink} onChange={(e) => setLocalLink(e.target.value)} />
                  <button className="btn btn-primary rounded-end" disabled={isUpdating || localLink === task.link} onClick={() => handleUpdateField('link', localLink)}>
                    Update
                  </button>
                </div>
                {task.link && <a href={task.link} target="_blank" rel="noreferrer" className="d-block mt-2 small text-truncate">{task.link}</a>}
              </div>
            </div>
          </div>

          {/* SAĞ PANEL: İletişim / Messaging */}
          <div className="col-lg-6 d-flex flex-column" style={{ minHeight: '400px' }}>
            <h6 className="fw-bold text-muted mb-3 text-uppercase" style={{ fontSize: '12px', letterSpacing: '1px' }}>Task Updates & Messages</h6>
            
            <div className="flex-grow-1 bg-light rounded-4 p-3 d-flex flex-column position-relative border" style={{ maxHeight: '450px' }}>
                
                {/* Mesaj Listesi */}
                <div className="flex-grow-1 overflow-auto pe-2 d-flex flex-column gap-3 mb-3" style={{ scrollbarWidth: 'thin' }}>
                    
                    {/* Görev Notu (Sabit İlk Mesaj gibi görünür) */}
                    {task.notes && (
                        <div className="align-self-start bg-white p-3 rounded-4 shadow-sm border border-info border-opacity-25 w-100">
                            <span className="d-block text-info small fw-bold mb-1">Task Instructions (Admin)</span>
                            <p className="mb-0 text-secondary" style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{task.notes}</p>
                        </div>
                    )}

                    {!task.notes && (!task.messages || task.messages.length === 0) && (
                        <div className="text-center text-muted my-auto small">No messages yet. Start the conversation!</div>
                    )}

                    {/* Dinamik Mesajlar */}
                    {task.messages && task.messages.map((msg, idx) => {
                        const isMe = msg.senderId === userData?.uid;
                        return (
                            <div key={idx} className={`d-flex flex-column ${isMe ? 'align-items-end' : 'align-items-start'}`}>
                                <span className="text-muted mb-1" style={{ fontSize: '11px' }}>
                                    <strong>{isMe ? 'You' : msg.senderName}</strong> • {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                                <div className={`p-2 px-3 rounded-4 shadow-sm ${isMe ? 'bg-primary text-white' : 'bg-white text-dark border'}`} style={{ maxWidth: '85%', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                                    {msg.text}
                                </div>
                            </div>
                        )
                    })}
                    <div ref={messagesEndRef} />
                </div>

                {/* Mesaj Gönderme Inputu */}
                <form onSubmit={handleSendMessage} className="mt-auto pt-2 border-top">
                    <div className="input-group bg-white rounded-pill p-1 shadow-sm border">
                        <input 
                            type="text" 
                            className="form-control border-0 bg-transparent shadow-none" 
                            placeholder="Type an update or question..." 
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={isUpdating}
                        />
                        <button type="submit" className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center" disabled={!message.trim() || isUpdating} style={{ width: '38px', height: '38px' }}>
                            <PaperAirplaneIcon style={{ width: '16px' }} />
                        </button>
                    </div>
                </form>
            </div>
          </div>

        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TaskViewModal;